import React, { useState } from 'react';
import { CurrencyInput } from '../CurrencyInput';

interface EmploymentInfoData {
  employmentStatus: 'Employed' | 'Self-Employed' | 'Business Owner' | 'Unemployed' | '';
  // For Employed
  employer?: string;
  occupation?: string;
  workAddress?: string;
  employmentStartDate?: string;
  monthlySalary?: string;
  // For Business Owners
  businessName?: string;
  businessType?: string;
  businessRegistrationNumber?: string;
  taxIdentificationNumber?: string;
  yearsInBusiness?: number;
  monthlyBusinessRevenue?: string;
}

interface EmploymentInfoStepProps {
  initialData?: Partial<EmploymentInfoData>;
  onNext: (data: EmploymentInfoData) => void;
  onBack: () => void;
}

const BUSINESS_TYPES = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company (LLC)',
  'Public Limited Company (PLC)',
  'Cooperative Society',
  'Non-Governmental Organization (NGO)',
  'Other',
];

export const EmploymentInfoStep: React.FC<EmploymentInfoStepProps> = ({
  initialData,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<EmploymentInfoData>({
    employmentStatus: initialData?.employmentStatus || '',
    employer: initialData?.employer || '',
    occupation: initialData?.occupation || '',
    workAddress: initialData?.workAddress || '',
    employmentStartDate: initialData?.employmentStartDate || '',
    monthlySalary: initialData?.monthlySalary || '',
    businessName: initialData?.businessName || '',
    businessType: initialData?.businessType || '',
    businessRegistrationNumber: initialData?.businessRegistrationNumber || '',
    taxIdentificationNumber: initialData?.taxIdentificationNumber || '',
    yearsInBusiness: initialData?.yearsInBusiness || undefined,
    monthlyBusinessRevenue: initialData?.monthlyBusinessRevenue || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof EmploymentInfoData, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof EmploymentInfoData>>(new Set());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof EmploymentInfoData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (field: keyof EmploymentInfoData) => {
    setTouchedFields((prev) => new Set(prev).add(field));
    validateField(field);
  };

  const validateField = (field: keyof EmploymentInfoData): string => {
    const value = formData[field];
    let error = '';
    const isEmployed = formData.employmentStatus === 'Employed';
    const isBusiness = formData.employmentStatus === 'Self-Employed' || formData.employmentStatus === 'Business Owner';

    switch (field) {
      case 'employmentStatus':
        if (!value) error = 'Employment status is required';
        break;

      // Employed fields
      case 'employer':
        if (isEmployed && (!value || (value as string).trim().length < 2)) {
          error = 'Employer name is required';
        }
        break;

      case 'occupation':
        if (isEmployed && (!value || (value as string).trim().length < 2)) {
          error = 'Occupation is required';
        }
        break;

      case 'workAddress':
        if (isEmployed && (!value || (value as string).trim().length < 10)) {
          error = 'Work address is required (at least 10 characters)';
        }
        break;

      case 'employmentStartDate':
        if (isEmployed && !value) {
          error = 'Employment start date is required';
        } else if (isEmployed && value) {
          const startDate = new Date(value as string);
          const today = new Date();
          if (startDate > today) {
            error = 'Employment start date cannot be in the future';
          }
        }
        break;

      case 'monthlySalary':
        if (isEmployed && (!value || parseFloat(value as string) <= 0)) {
          error = 'Monthly salary is required and must be greater than 0';
        }
        break;

      // Business fields
      case 'businessName':
        if (isBusiness && (!value || (value as string).trim().length < 2)) {
          error = 'Business name is required';
        }
        break;

      case 'businessType':
        if (isBusiness && !value) {
          error = 'Business type is required';
        }
        break;

      case 'businessRegistrationNumber':
        if (isBusiness && formData.businessType !== 'Sole Proprietorship' && !value) {
          error = 'Business registration number (CAC) is required for registered businesses';
        }
        break;

      case 'taxIdentificationNumber':
        if (isBusiness && (!value || (value as string).trim().length < 5)) {
          error = 'Tax Identification Number (TIN) is required';
        }
        break;

      case 'yearsInBusiness':
        if (isBusiness) {
          const years = Number(value);
          if (!value || isNaN(years) || years < 0) {
            error = 'Years in business is required and must be 0 or greater';
          } else if (years > 100) {
            error = 'Please enter a valid number of years';
          }
        }
        break;

      case 'monthlyBusinessRevenue':
        if (isBusiness && (!value || parseFloat(value as string) <= 0)) {
          error = 'Monthly business revenue is required and must be greater than 0';
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const validateAll = (): boolean => {
    const isEmployed = formData.employmentStatus === 'Employed';
    const isBusiness = formData.employmentStatus === 'Self-Employed' || formData.employmentStatus === 'Business Owner';
    const isUnemployed = formData.employmentStatus === 'Unemployed';

    const fieldsToValidate: (keyof EmploymentInfoData)[] = ['employmentStatus'];

    if (isEmployed) {
      fieldsToValidate.push('employer', 'occupation', 'workAddress', 'employmentStartDate', 'monthlySalary');
    } else if (isBusiness) {
      fieldsToValidate.push(
        'businessName',
        'businessType',
        'taxIdentificationNumber',
        'yearsInBusiness',
        'monthlyBusinessRevenue'
      );
      // Only validate CAC for non-sole proprietorships
      if (formData.businessType && formData.businessType !== 'Sole Proprietorship') {
        fieldsToValidate.push('businessRegistrationNumber');
      }
    } else if (isUnemployed) {
      // For unemployed, we just need employment status
      // They might still apply for loans using other income sources
    }

    const newErrors: Partial<Record<keyof EmploymentInfoData, string>> = {};
    let hasErrors = false;

    fieldsToValidate.forEach((field) => {
      const error = validateField(field);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setTouchedFields(new Set(fieldsToValidate));

    return !hasErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateAll()) {
      onNext(formData);
    }
  };

  const isEmployed = formData.employmentStatus === 'Employed';
  const isBusiness = formData.employmentStatus === 'Self-Employed' || formData.employmentStatus === 'Business Owner';
  const isUnemployed = formData.employmentStatus === 'Unemployed';

  return (
    <form onSubmit={handleSubmit} className="application-form">
      <div className="form-section">
        <h3>Employment & Business Information</h3>
        <p className="section-description">
          Please provide information about your employment or business. Fields marked with * are required.
        </p>

        {/* Employment Status */}
        <div className="form-group">
          <label htmlFor="employmentStatus">Employment Status *</label>
          <select
            id="employmentStatus"
            name="employmentStatus"
            value={formData.employmentStatus}
            onChange={handleChange}
            onBlur={() => handleBlur('employmentStatus')}
            required
            className={touchedFields.has('employmentStatus') && errors.employmentStatus ? 'error' : ''}
          >
            <option value="">Select employment status</option>
            <option value="Employed">Employed (Salaried Worker)</option>
            <option value="Self-Employed">Self-Employed</option>
            <option value="Business Owner">Business Owner</option>
            <option value="Unemployed">Unemployed</option>
          </select>
          {touchedFields.has('employmentStatus') && errors.employmentStatus && (
            <span className="field-error">{errors.employmentStatus}</span>
          )}
        </div>

        {/* Fields for Employed */}
        {isEmployed && (
          <>
            <h4 className="subsection-title">Employment Details</h4>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="employer">Employer Name *</label>
                <input
                  type="text"
                  id="employer"
                  name="employer"
                  value={formData.employer}
                  onChange={handleChange}
                  onBlur={() => handleBlur('employer')}
                  required
                  placeholder="Enter employer name"
                  className={touchedFields.has('employer') && errors.employer ? 'error' : ''}
                />
                {touchedFields.has('employer') && errors.employer && (
                  <span className="field-error">{errors.employer}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="occupation">Occupation / Job Title *</label>
                <input
                  type="text"
                  id="occupation"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                  onBlur={() => handleBlur('occupation')}
                  required
                  placeholder="e.g., Software Engineer, Accountant"
                  className={touchedFields.has('occupation') && errors.occupation ? 'error' : ''}
                />
                {touchedFields.has('occupation') && errors.occupation && (
                  <span className="field-error">{errors.occupation}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="workAddress">Work Address *</label>
              <textarea
                id="workAddress"
                name="workAddress"
                value={formData.workAddress}
                onChange={handleChange}
                onBlur={() => handleBlur('workAddress')}
                rows={2}
                required
                placeholder="Enter your workplace address"
                className={touchedFields.has('workAddress') && errors.workAddress ? 'error' : ''}
              />
              {touchedFields.has('workAddress') && errors.workAddress && (
                <span className="field-error">{errors.workAddress}</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="employmentStartDate">Employment Start Date *</label>
                <input
                  type="date"
                  id="employmentStartDate"
                  name="employmentStartDate"
                  value={formData.employmentStartDate}
                  onChange={handleChange}
                  onBlur={() => handleBlur('employmentStartDate')}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className={touchedFields.has('employmentStartDate') && errors.employmentStartDate ? 'error' : ''}
                />
                {touchedFields.has('employmentStartDate') && errors.employmentStartDate && (
                  <span className="field-error">{errors.employmentStartDate}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="monthlySalary">Monthly Salary (₦) *</label>
                <CurrencyInput
                  id="monthlySalary"
                  name="monthlySalary"
                  value={formData.monthlySalary || ''}
                  onChange={(value) => {
                    setFormData({ ...formData, monthlySalary: value.toString() });
                    if (errors.monthlySalary) {
                      setErrors({ ...errors, monthlySalary: '' });
                    }
                  }}
                  onBlur={() => handleBlur('monthlySalary')}
                  required
                  min={0}
                  placeholder="Enter your monthly salary"
                  error={touchedFields.has('monthlySalary') ? errors.monthlySalary : ''}
                  showLabel={false}
                />
              </div>
            </div>
          </>
        )}

        {/* Fields for Business Owners / Self-Employed */}
        {isBusiness && (
          <>
            <h4 className="subsection-title">Business Details</h4>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessName">Business Name *</label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('businessName')}
                  required
                  placeholder="Enter business name"
                  className={touchedFields.has('businessName') && errors.businessName ? 'error' : ''}
                />
                {touchedFields.has('businessName') && errors.businessName && (
                  <span className="field-error">{errors.businessName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="businessType">Business Type *</label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  onBlur={() => handleBlur('businessType')}
                  required
                  className={touchedFields.has('businessType') && errors.businessType ? 'error' : ''}
                >
                  <option value="">Select business type</option>
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {touchedFields.has('businessType') && errors.businessType && (
                  <span className="field-error">{errors.businessType}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessRegistrationNumber">
                  Business Registration Number (CAC) {formData.businessType !== 'Sole Proprietorship' && '*'}
                </label>
                <input
                  type="text"
                  id="businessRegistrationNumber"
                  name="businessRegistrationNumber"
                  value={formData.businessRegistrationNumber}
                  onChange={handleChange}
                  onBlur={() => handleBlur('businessRegistrationNumber')}
                  required={formData.businessType !== 'Sole Proprietorship'}
                  placeholder="Enter CAC registration number"
                  className={
                    touchedFields.has('businessRegistrationNumber') && errors.businessRegistrationNumber ? 'error' : ''
                  }
                />
                {touchedFields.has('businessRegistrationNumber') && errors.businessRegistrationNumber && (
                  <span className="field-error">{errors.businessRegistrationNumber}</span>
                )}
                {formData.businessType === 'Sole Proprietorship' && (
                  <span className="form-hint">Optional for sole proprietorships</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="taxIdentificationNumber">Tax Identification Number (TIN) *</label>
                <input
                  type="text"
                  id="taxIdentificationNumber"
                  name="taxIdentificationNumber"
                  value={formData.taxIdentificationNumber}
                  onChange={handleChange}
                  onBlur={() => handleBlur('taxIdentificationNumber')}
                  required
                  placeholder="Enter TIN"
                  className={
                    touchedFields.has('taxIdentificationNumber') && errors.taxIdentificationNumber ? 'error' : ''
                  }
                />
                {touchedFields.has('taxIdentificationNumber') && errors.taxIdentificationNumber && (
                  <span className="field-error">{errors.taxIdentificationNumber}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="yearsInBusiness">Years in Business *</label>
                <input
                  type="number"
                  id="yearsInBusiness"
                  name="yearsInBusiness"
                  value={formData.yearsInBusiness || ''}
                  onChange={handleChange}
                  onBlur={() => handleBlur('yearsInBusiness')}
                  min="0"
                  max="100"
                  required
                  placeholder="Enter number of years"
                  className={touchedFields.has('yearsInBusiness') && errors.yearsInBusiness ? 'error' : ''}
                />
                {touchedFields.has('yearsInBusiness') && errors.yearsInBusiness && (
                  <span className="field-error">{errors.yearsInBusiness}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="monthlyBusinessRevenue">Average Monthly Revenue (₦) *</label>
                <CurrencyInput
                  id="monthlyBusinessRevenue"
                  name="monthlyBusinessRevenue"
                  value={formData.monthlyBusinessRevenue || ''}
                  onChange={(value) => {
                    setFormData({ ...formData, monthlyBusinessRevenue: value.toString() });
                    if (errors.monthlyBusinessRevenue) {
                      setErrors({ ...errors, monthlyBusinessRevenue: '' });
                    }
                  }}
                  onBlur={() => handleBlur('monthlyBusinessRevenue')}
                  required
                  min={0}
                  placeholder="Enter average monthly revenue"
                  error={touchedFields.has('monthlyBusinessRevenue') ? errors.monthlyBusinessRevenue : ''}
                  showLabel={false}
                />
              </div>
            </div>
          </>
        )}

        {/* Message for Unemployed */}
        {isUnemployed && (
          <div className="info-message">
            <p>
              <strong>Note:</strong> You can still apply for a loan if you have other sources of income. Please provide details about your income sources in the next step.
            </p>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onBack} className="btn btn-secondary">
          Back
        </button>
        <button type="submit" className="btn btn-primary">
          Continue to Financial Information
        </button>
      </div>
    </form>
  );
};
